'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useQueryStates } from 'nuqs';
import { Button, HorizontalTabs } from '~/components/primitives';
import { ActivityCardGrid_EmptyState, ActivityCardGrid_NotFound } from '~/features/activities/ActivityCardGrid';
import { ActivityCard } from '~/features/activities/ActivityCard';
import { ActivityCardGrid_InfiniteScroll } from '~/features/activities/search/client';
import { useActivitySearchCtx } from '~/features/activities/search/client/context';
import { useI18n } from '~/features/i18n';
import { Catalog_EditMode_Card } from './card';
import { Catalog_BrowseMode_Sidebar, Catalog_EditMode_Sidebar } from './sidebar';
import { Catalog_EditMode_SaveButton } from './utils';
import { useCatalog_EditMode_Session, useCatalog_EditMode_TabCounts, useCatalog_BookingGridQuery, VIEW_TAB_QP } from './session';
import type { ClassValue } from 'clsx';
import { cx } from 'class-variance-authority';

/** Top-level results section for the BIZZ booking page: sidebar (browse or
 * edit) + content column (view tabs in edit mode, results count, grid). The
 * page tree composes this directly — no slot props, no wrapper over
 * `<ActivityFilterResults>`. The grid is the PLP's virtualized infinite-scroll
 * grid driven by this page's own query and cards. */
export const Catalog_BookingResults: React.FC = () => {
  const session = useCatalog_EditMode_Session();
  const isEditing = session.step !== null;
  const grid = useCatalog_BookingGridQuery();
  const ctx = useActivitySearchCtx();
  const filters = ctx.useFilters();
  const i18n = useI18n();
  const minParticipants = filters.value.participants ?? null;
  const isInclusiveEmpty = isEditing && session.rules.activityFilters.groupIDs.mode === 'INCLUSIVE' && (session.rules.activityFilters.groupIDs.IDs?.length ?? 0) === 0;
  return <div className={cx('grid min-h-[calc(100dvh-4.5rem)] gap-4 xl:gap-6',
  // Edit mode gets a wider first column: its footer carries the
  // two-button "Discard / Save & finetune selection" row, which
  // wraps at the browse-mode width.
  isEditing ? 'lg:grid-cols-[minmax(320px,360px)_minmax(0,1fr)] xl:grid-cols-[minmax(360px,420px)_minmax(0,1fr)]' : 'lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]')}>
            {isEditing ? <Catalog_EditMode_Sidebar totalResults={grid.totalResults} isFetching={grid.isFetching} /> : <Catalog_BrowseMode_Sidebar totalResults={grid.totalResults} isFetching={grid.isFetching} />}
            <div className="min-w-0 lg:self-start">
                {session.step === 'selection' && <ViewTabsHeader className="mb-4" />}
                {/* Mobile results-count header: in browse mode the sidebar is
                 * hidden below `lg`, so surface the count here. Edit mode is
                 * desktop-only — no mobile header needed. */}
                <header className="-mx-1 mb-2 flex items-center justify-between gap-3 bg-brown-50 px-1 py-4 text-blue md:py-2 lg:hidden">
                    <span data-testid="activity-plp-results-count" className="text-sm font-medium sm:text-base">
                        {i18n._('{count} teambuildings found', {
            count: grid.totalResults ?? '-'
          })}
                    </span>
                </header>
                {grid.totalResults === 0 ? isInclusiveEmpty ? <Catalog_EditMode_InclusiveEmptyPlaceholder /> : <ActivityCardGrid_NotFound /> :
      // A filter or tab change refetches behind `keepPreviousData`;
      // dim the stale grid instead of dropping back to skeletons.
      // Opacity, not `animate-pulse`: the grid can be tall and a
      // repainting animation over it is expensive.
      <div className={cx('transition-opacity duration-300', grid.isFetching && !grid.infiniteQueryResult.isFetchingNextPage && 'pointer-events-none opacity-50')}>
                        <ActivityCardGrid_InfiniteScroll infiniteQueryResult={grid.infiniteQueryResult} allActivities={grid.activities ?? []} minParticipants={minParticipants} pricedParticipants={undefined} cardVariant="control" weightSet="control"
        // Both catalog cards are taller than the PLP's: the
        // edit card carries the Add/Remove footer. Rows sized
        // by the PLP formula clip it away entirely.
        measureRows
        // Both modes render their own card. Browse mode looks like
        // the PLP but must not inherit its click tracking or its
        // open-in-new-tab behaviour: a bizz admin builds a catalog
        // in one tab.
        renderCard={activity => isEditing ? <Catalog_EditMode_Card activity={activity} selectedParticipants={minParticipants} /> : <ActivityCard {...activity} selectedParticipants={minParticipants} />} />
                    </div>}
            </div>
        </div>;
};

/** View-tab strip rendered above the activity grid in step 2. Reads the
 * current catalog mode from the LIVE rules state: EXCLUSIVE shows a
 * "Manually excluded" tab, while INCLUSIVE hides it entirely (there the manual
 * selection is identical to "Current catalog").
 *
 * Sticky like the edit-mode sidebar so the tabs + Save & publish button stay
 * reachable while scrolling the grid. `top-24` matches the sidebar offset.
 * Save & publish persists and then leaves edit mode (`session.saveAndExit`). */
const ViewTabsHeader: React.FC<{
  className?: ClassValue;
}> = props => {
  const $ = _c(36);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  const tabCounts = useCatalog_EditMode_TabCounts();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      view: VIEW_TAB_QP
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [tabState, setTabState] = useQueryStates(t0);
  const mode = session.rules.activityFilters.groupIDs.mode;
  let t1;
  let t2;
  if ($[1] !== mode || $[2] !== setTabState || $[3] !== tabState.view) {
    t1 = () => {
      if (mode === "INCLUSIVE" && tabState.view === "selected") {
        setTabState({
          view: "visible"
        });
      }
    };
    t2 = [mode, tabState.view, setTabState];
    $[1] = mode;
    $[2] = setTabState;
    $[3] = tabState.view;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== props.className) {
    t3 = cx("top-24 z-2 flex items-center justify-between gap-3 rounded-3xl bg-white p-4 shadow-sm lg:sticky", props.className);
    $[6] = props.className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = tabState.view;
  let t5;
  if ($[8] !== setTabState) {
    t5 = next => setTabState({
      view: next
    });
    $[8] = setTabState;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== i18n) {
    t6 = i18n._("bizz.booking.edit.viewTabs.currentCatalog", undefined, {
      message: "Current catalog"
    });
    $[10] = i18n;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = {
      value: "visible" as const,
      label: t6
    };
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== i18n || $[15] !== mode) {
    t8 = mode === "EXCLUSIVE" ? [{
      value: "selected" as const,
      label: i18n._("bizz.booking.edit.viewTabs.manuallyExcluded", undefined, {
        message: "Manually excluded"
      })
    }] : [];
    $[14] = i18n;
    $[15] = mode;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== i18n) {
    t9 = i18n._("bizz.booking.edit.viewTabs.all", undefined, {
      message: "All"
    });
    $[17] = i18n;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = {
      value: "all" as const,
      label: t9
    };
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7 || $[23] !== t8) {
    t11 = [t7, ...t8, t10];
    $[21] = t10;
    $[22] = t7;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== tabCounts.counts) {
    t12 = t13 => {
      const {
        tab,
        isActive
      } = t13;
      return <span className="flex items-center gap-2"><span>{tab.label}</span><span className={isActive ? "rounded-full bg-brown-100 px-2 py-0.5 text-xs text-tertiary" : "rounded-full bg-brown-200/60 px-2 py-0.5 text-xs text-tertiary"}>{tabCounts.counts[tab.value]}</span></span>;
    };
    $[25] = tabCounts.counts;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t5 || $[30] !== tabState.view) {
    t13 = <HorizontalTabs.StateLess size="small" value={t4} onChange={t5} className="bg-brown-100! max-lg:hidden" tabs={t11} renderTab={t12} />;
    $[27] = t11;
    $[28] = t12;
    $[29] = t5;
    $[30] = tabState.view;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Catalog_EditMode_SaveButton testId="bizz-booking-view-tabs-save" />;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t13 || $[34] !== t3) {
    t15 = <div className={t3}>{t13}{t14}</div>;
    $[33] = t13;
    $[34] = t3;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};

/** Empty-state preset: INCLUSIVE mode with zero manually-selected IDs.
 * Makes it clear that the empty grid is expected and a selection still
 * has to be made; CTA jumps to step 2 + the "All" view tab so the admin
 * lands directly on the screen where they can start adding activities. */
const Catalog_EditMode_InclusiveEmptyPlaceholder: React.FC = () => {
  const $ = _c(17);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      view: VIEW_TAB_QP
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setView] = useQueryStates(t0);
  let t1;
  if ($[1] !== i18n) {
    t1 = i18n._("bizz.booking.edit.inclusiveEmpty.title", undefined, {
      message: "Your catalog is empty"
    });
    $[1] = i18n;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== i18n) {
    t2 = i18n._("bizz.booking.edit.inclusiveEmpty.description", undefined, {
      message: "In Inclusive mode the catalog stays empty until you pick activities. Switch to the manual-selection step to add them."
    });
    $[3] = i18n;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== session || $[6] !== setView) {
    t3 = () => {
      session.setStep("selection");
      setView({
        view: "all"
      });
    };
    $[5] = session;
    $[6] = setView;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== i18n) {
    t4 = i18n._("bizz.booking.edit.wizard.nextStep", undefined, {
      message: "Finetune selection"
    });
    $[8] = i18n;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Button iconAfter="chevron-right" variant="tertiary" onClick={t3}>{t4}</Button>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t5) {
    t6 = <ActivityCardGrid_EmptyState title={t1} description={t2} ctaButton={t5} />;
    $[13] = t1;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};